import React, { useState, useMemo, useRef } from "react";
import {
  CheckCircle2,
  ChevronRight,
  Clock,
  Copy,
  Info,
  MessageCircle,
  Mic,
  Package,
  Plus,
  Printer,
  Receipt,
  RefreshCw,
  Send,
  Trash2,
  TrendingUp,
  Wrench,
  XCircle,
} from "lucide-react";
import { VozOrcamento } from "./os";
import { abrirWhatsApp, copiar, Btn, Busca, CabecalhoFolha, Field, ItensFolha, Modal, Pill, Placa, Stat, Vazio, usarServidor } from "../ui/componentes";
import { MOTIVOS_RECUSA, STO, brl, dias, fdata, uid } from "../ui/formato";

/* ══════════════════════════════════════════════════════════════════
   ORÇAMENTOS DE BALCÃO
   O cliente liga perguntando quanto custa. Não tem carro no pátio,
   às vezes nem cadastro — e metade nunca vira serviço. Por isso vive
   separado da OS: junto, polui o pátio e esconde a taxa de conversão.
   ══════════════════════════════════════════════════════════════════ */
function Orcamentos({ db, set, toast, usuario, abrirOS, acoes }) {
  const { executar, ocupado } = usarServidor(toast);
  const [filtro, setFiltro] = useState("todos");
  const [q, setQ] = useState("");
  const [aberto, setAberto] = useState(null);
  const [addItem, setAddItem] = useState(null);
  const [busca, setBusca] = useState("");
  const [voz, setVoz] = useState(false);
  const [zap, setZap] = useState(null);
  const [recusa, setRecusa] = useState(null);
  const [converter, setConverter] = useState(null);
  const [placa, setPlaca] = useState("");
  // "proprietario" é quem abriu a oficina — mesmo poder que admin. Sem
  // incluir aqui, o dono não enxergava importação, margem nem exclusão,
  // e o servidor aceitaria as três (exigirPapel já trata os dois iguais).
  const admin = ["admin", "proprietario"].includes(usuario.papel);

  const total = (o) => o.itens.reduce((s, i) => s + i.qtd * i.valor, 0) - (o.desconto || 0);
  const custo = (o) => o.itens.reduce((s, i) => s + i.qtd * (i.custo || 0), 0);
  const vence = (o) => { const d = new Date(o.criado); d.setDate(d.getDate() + o.validade); return d.toISOString().slice(0, 10); };
  const situacao = (o) => (o.status === "enviado" && vence(o) < dias(0) ? "expirado" : o.status);

  const salvarDepois = useRef({});
  const mudar = (id, fn) => {
    set((d) => ({ ...d, orcamentos: d.orcamentos.map((o) => (o.id === id ? fn(o) : o)) }));
    const alvo = db.orcamentos.find((o) => o.id === id);
    if (!alvo?._id) return;
    clearTimeout(salvarDepois.current[id]);
    salvarDepois.current[id] = setTimeout(() => {
      const a = fn(alvo);
      acoes.atualizarOrcamento(alvo._id, {
        nome_contato: a.nome, telefone_contato: a.tel, veiculo_descricao: a.veiculo,
        validade_dias: +a.validade || 15, desconto: +a.desconto || 0, observacao: a.obs,
        status: a.status,
      }).catch(() => toast("Não salvou", "A alteração ficou só nesta tela.", "alerta"));
    }, 800);
  };
  const orc = aberto ? db.orcamentos.find((o) => o.id === aberto) : null;

  /* funil do mês — o número que a oficina nunca teve */
  const funil = useMemo(() => {
    const recentes = db.orcamentos.filter((o) => o.criado >= dias(-60));
    const respondidos = recentes.filter((o) => ["aprovado", "recusado"].includes(o.status));
    const aprovados = recentes.filter((o) => o.status === "aprovado");
    const abertos = recentes.filter((o) => situacao(o) === "enviado");
    const perdidos = recentes.filter((o) => o.status === "recusado");
    const porMotivo = {};
    for (const o of perdidos) if (o.motivo) porMotivo[o.motivo] = (porMotivo[o.motivo] || 0) + 1;
    return {
      enviados: recentes.length, aprovados: aprovados.length,
      taxa: respondidos.length ? (aprovados.length / respondidos.length) * 100 : 0,
      emAberto: abertos.reduce((s, o) => s + total(o), 0), qtdAberto: abertos.length,
      ganho: aprovados.reduce((s, o) => s + total(o), 0),
      perdido: perdidos.reduce((s, o) => s + total(o), 0),
      porMotivo,
    };
  }, [db.orcamentos]);

  /* O número vem do servidor: gerar aqui daria repetição assim que duas
     pessoas criassem orçamento ao mesmo tempo.

     E o botão trava enquanto o servidor responde. Sem isso o segundo toque
     criava outro orçamento: os quatro do banco nasceram em dois pares, com
     um segundo entre cada, de gente tocando de novo porque nada apareceu
     na tela. */
  const novo = () => executar(
    () => acoes.criarOrcamento({ nome: "", tel: "", veiculo: "", validade: 15, desconto: 0, obs: "" }),
    (o) => setAberto(o._id),
    "Não deu para criar o orçamento");

  /* Grava no servidor. Antes só mexia no estado da tela, e o PATCH do
     orçamento salvava contato, veículo, validade e desconto — nunca os
     itens. O orçamento ficava pronto na tela e vazio no banco. */
  const addLinha = (item) => executar(
    () => acoes.addItemOrcamento(orc._id ?? orc.id, item),
    null, "Não deu para adicionar o item");

  const tirarLinha = (itemId) => executar(
    () => acoes.removerItemOrcamento(orc._id ?? orc.id, itemId),
    null, "Não deu para remover o item");

  /* Quantidade digitada: a tela responde na hora e o servidor recebe depois
     de uma pausa — salvar a cada tecla seria uma requisição por dígito. */
  const salvarItemDepois = useRef({});
  const mudarQtd = (itemId, qtd) => {
    mudar(orc.id, (o) => ({ ...o, itens: o.itens.map((x) => (x.id === itemId ? { ...x, qtd } : x)) }));
    clearTimeout(salvarItemDepois.current[itemId]);
    salvarItemDepois.current[itemId] = setTimeout(() => {
      acoes.atualizarItemOrcamento(orc._id ?? orc.id, itemId, { quantidade: qtd })
        .catch((e) => toast("Não salvou a quantidade", e?.message ?? "Ficou só nesta tela.", "alerta"));
    }, 700);
  };
  const aplicarVoz = (itens) => itens.forEach((i) => {
    const cat = i.tipo === "peca" ? db.pecas.find((p) => p.id === i.ref) : db.servicos.find((x) => x.id === i.ref);
    addLinha({ tipo: i.tipo, ref: i.ref || null, desc: cat ? cat.nome : i.desc, qtd: i.qtd,
      valor: cat ? (i.tipo === "peca" ? cat.venda : cat.valor) : i.valor,
      custo: cat && i.tipo === "peca" ? cat.custo : 0 });
  });

  const mensagem = (o) => {
    const pecas = o.itens.filter((i) => i.tipo === "peca");
    const servs = o.itens.filter((i) => i.tipo === "servico");
    const linha = (i) => `${i.qtd}× ${i.desc} — ${brl(i.qtd * i.valor)}`;
    return [
      // Do cadastro da oficina. Era EMPRESA.fantasia — fixo do protótipo —
      // então o orçamento saía para o cliente assinado como outra empresa,
      // com telefone de outra cidade. É a única mensagem que vai para fora;
      // errar aqui é errar na frente de quem paga.
      `*${db.oficina?.nome_fantasia || db.oficina?.razao_social || "Orçamento"}*`,
      `Orçamento ${o.id} · válido até ${fdata(vence(o))}`,
      ``,
      `Veículo: ${o.veiculo || "—"}`,
      ...(pecas.length ? ["", "*Peças*", ...pecas.map(linha)] : []),
      ...(servs.length ? ["", "*Serviços*", ...servs.map(linha)] : []),
      ...(o.desconto ? ["", `Desconto: −${brl(o.desconto)}`] : []),
      ``,
      `*Total: ${brl(total(o))}*`,
      ``,
      `Para aprovar, é só responder SIM que já reservamos as peças.`,
      // Só entra se estiver cadastrado. Telefone errado numa mensagem que vai
      // para o cliente é pior que nenhum telefone.
      ...(db.oficina?.telefone ? [db.oficina.telefone] : []),
    ].join("\n");
  };

  const enviar = () => {
    if (!orc.nome.trim() || !orc.tel.trim()) { toast("Falta nome ou telefone", "Sem contato não dá para enviar nem cobrar retorno.", "erro"); return; }
    if (orc.itens.length === 0) { toast("Orçamento vazio", "Adicione ao menos um item antes de enviar.", "erro"); return; }
    // Abre a conversa ANTES de marcar como enviado: se não houver telefone
    // válido, o orçamento não pode ficar "aguardando cliente" por engano.
    if (!abrirWhatsApp(orc.tel, mensagem(orc), toast)) return;
    mudar(orc.id, (o) => ({ ...o, status: "enviado", enviado: dias(0) }));
    setZap(orc.id);
  };

  const confirmarRecusa = (motivo) => {
    const o = db.orcamentos.find((x) => x.id === recusa);
    executar(
      () => acoes.recusarOrcamento(o?._id ?? recusa, motivo),
      () => {
        setRecusa(null); setAberto(null);
        toast("Recusa registrada",
          "O motivo entra no relatório — é ele que mostra se o problema é preço, prazo ou concorrência.");
      },
      "Não deu para registrar");
  };

  /* Aprovar vira OS: cria cliente e veículo se ainda não existirem, e a
     partir daí o fluxo é o de sempre — estoque, execução, nota. */
  /**
   * Aprovar vira OS no servidor, numa transação só: cria cliente e veículo
   * se ainda não existirem, copia os itens, lança o título e reserva as
   * peças. Fazer isso em partes deixaria estado pela metade se algo falhar
   * no meio — cliente criado, OS não.
   */
  const converterEmOS = () => {
    const o = db.orcamentos.find((x) => x.id === converter);
    const p = placa.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!p) { toast("Falta a placa", "O carro está chegando: a placa identifica o veículo em todo o sistema.", "erro"); return; }

    executar(
      () => acoes.aprovarOrcamento(o?._id ?? converter, p),
      (r) => {
        setConverter(null); setPlaca(""); setAberto(null);
        toast("Orçamento aprovado",
          `Virou a OS ${r.numero}. Cliente e veículo já ficaram cadastrados.`);
      },
      "Não deu para aprovar");
  };

  const lista = db.orcamentos.filter((o) => {
    if (filtro !== "todos" && situacao(o) !== filtro) return false;
    const t = q.toLowerCase();
    return !t || o.id.toLowerCase().includes(t) || o.nome.toLowerCase().includes(t) ||
      (o.veiculo || "").toLowerCase().includes(t) || o.tel.includes(t);
  });

  const abas = [["todos", "Todos"], ...Object.entries(STO).map(([k, v]) => [k, v.l])];

  return (
    <div className="grid gap-4">
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))" }}>
        <Stat label="Taxa de conversão" value={`${funil.taxa.toFixed(0)}%`}
          tone={funil.taxa >= 50 ? "ok" : "signal"} icon={TrendingUp}
          sub={`${funil.aprovados} de ${funil.enviados} orçamentos viraram serviço`} />
        <Stat label="Esperando resposta" value={brl(funil.emAberto)} tone="signal" icon={Clock}
          sub={`${funil.qtdAberto} orçamento(s) sem retorno`} />
        <Stat label="Fechado no período" value={brl(funil.ganho)} tone="ok" icon={CheckCircle2} />
        <Stat label="Perdido no período" value={brl(funil.perdido)} tone="crit" icon={XCircle}
          sub={Object.keys(funil.porMotivo).length
            ? `principal motivo: ${MOTIVOS_RECUSA.find(([k]) => k === Object.entries(funil.porMotivo).sort((a, b) => b[1] - a[1])[0][0])?.[1].toLowerCase()}`
            : "nenhuma recusa registrada"} />
      </div>

      {funil.qtdAberto > 0 && (
        <div className="card p-3.5 flex flex-wrap items-center gap-3" style={{ borderLeft: "3px solid var(--signal)" }}>
          <MessageCircle size={16} className="t-signal" />
          <span className="text-sm flex-1">
            <span className="font-semibold">{funil.qtdAberto} orçamento(s) sem resposta</span>, somando {brl(funil.emAberto)}.
            Orçamento parado vira orçamento vencido — e o cliente resolve em outro lugar.
          </span>
          <Btn variant="o" size="xs" icon={Send}
            onClick={() => toast("Um de cada vez, por enquanto",
              "O WhatsApp abre uma conversa por vez. Abra o orçamento e use o botão de enviar em cada um.", "alerta")}>
            Cobrar todos
          </Btn>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Busca v={q} set={setQ} ph="Buscar por número, cliente, telefone ou veículo…" />
        <Btn variant="p" icon={Plus} disabled={ocupado} onClick={novo}>Novo orçamento</Btn>
      </div>

      <div className="flex gap-1 scroll" style={{ overflowX: "auto", borderBottom: "1px solid var(--line)" }}>
        {abas.map(([k, l]) => {
          const n = k === "todos" ? db.orcamentos.length : db.orcamentos.filter((o) => situacao(o) === k).length;
          return <button key={k} className={`tab ${filtro === k ? "on" : ""}`} onClick={() => setFiltro(k)}>{l} <span className="mono t-mut2">{n}</span></button>;
        })}
      </div>

      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead><tr><th>Número</th><th>Cliente</th><th>Veículo</th><th>Criado</th><th>Situação</th><th className="num">Total</th><th></th></tr></thead>
            <tbody>
              {lista.map((o) => {
                const st = situacao(o);
                const venceEm = Math.round((new Date(vence(o)) - new Date(dias(0))) / 86400000);
                return (
                  <tr key={o.id} style={{ cursor: "pointer" }} onClick={() => setAberto(o.id)}>
                    <td className="mono text-sm font-semibold">{o.id}</td>
                    <td>
                      <span className="block text-sm font-medium">{o.nome || "—"}</span>
                      <span className="block text-xs t-mut mono">{o.tel}{!o.cli && o.nome ? " · sem cadastro" : ""}</span>
                    </td>
                    <td className="text-sm t-mut">{o.veiculo || "—"}</td>
                    <td className="text-sm t-mut mono">{fdata(o.criado)}</td>
                    <td>
                      <Pill t={STO[st].c}>{STO[st].l}</Pill>
                      {st === "enviado" && venceEm <= 3 && <span className="block text-xs t-signal mt-0.5">vence em {venceEm}d</span>}
                      {o.os && <span className="block text-xs t-mut mono mt-0.5">{o.os}</span>}
                    </td>
                    <td className="num text-sm font-semibold">{brl(total(o))}</td>
                    <td><ChevronRight size={15} className="t-mut2" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {lista.length === 0 && <Vazio icon={Receipt} titulo="Nenhum orçamento aqui"
          msg="O cliente ligou perguntando o preço? Faça o orçamento sem precisar do carro no pátio."
          acao={<Btn variant="p" icon={Plus} disabled={ocupado} onClick={novo}>Novo orçamento</Btn>} />}
      </div>

      {Object.keys(funil.porMotivo).length > 0 && (
        <div className="card p-4">
          <div className="dsp mb-1" style={{ fontSize: 15 }}>Por que os clientes não fecham</div>
          <div className="text-xs t-mut mb-3">Preço, prazo e concorrência são problemas diferentes. Sem registrar, viram só "mês fraco".</div>
          <div className="grid gap-2">
            {Object.entries(funil.porMotivo).sort((a, b) => b[1] - a[1]).map(([k, n]) => {
              const maior = Math.max(...Object.values(funil.porMotivo));
              return (
                <div key={k} className="flex items-center gap-3">
                  <span className="text-sm" style={{ width: 168 }}>{MOTIVOS_RECUSA.find(([m]) => m === k)?.[1] ?? k}</span>
                  <div className="bar flex-1"><i style={{ width: `${(n / maior) * 100}%`, background: "var(--crit)" }} /></div>
                  <span className="mono text-sm font-semibold" style={{ width: 24, textAlign: "right" }}>{n}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {orc && (
        <div className="folha">
          <CabecalhoFolha of={db.oficina} titulo="Orçamento" numero={orc.id}
            data={`Emitido em ${fdata(orc.criado)} · válido até ${fdata(vence(orc))}`} />

          <h2>Cliente e veículo</h2>
          <div className="par">
            <div><b>Cliente:</b> {orc.nome || db.clientes.find((x) => x.id === orc.cli)?.nome || "—"}</div>
            <div><b>Telefone:</b> {orc.tel || db.clientes.find((x) => x.id === orc.cli)?.tel || "—"}</div>
            <div><b>Veículo:</b> {orc.veiculo || "—"}</div>
            <div><b>Validade:</b> {orc.validade} dias</div>
          </div>

          <ItensFolha itens={orc.itens} desconto={orc.desconto} />

          {orc.obs && <><h2>Observações</h2><div className="obs">{orc.obs}</div></>}

          <div className="ass">
            <div>Assinatura do cliente — de acordo</div>
            <div>Responsável pela oficina</div>
          </div>

          <div className="rodape">
            Valores válidos por {orc.validade} dias, sujeitos à confirmação de disponibilidade das peças.
            A aprovação reserva as peças para este serviço. Este documento não é nota fiscal.
          </div>
        </div>
      )}

      {/* editor */}
      <Modal open={!!orc} onClose={() => setAberto(null)} w="max-w-3xl"
        title={orc ? `Orçamento ${orc.id}` : ""}
        sub={orc ? (orc.status === "rascunho" ? "Sem carro no pátio e sem cadastro obrigatório — só o essencial para passar o preço."
          : `${STO[situacao(orc)].l} · válido até ${fdata(vence(orc))}`) : ""}
        footer={orc && <>
          <Btn variant="g" onClick={() => setAberto(null)}>Fechar</Btn>
          {["rascunho", "enviado"].includes(situacao(orc)) && <>
            <Btn variant="o" icon={Printer} onClick={() => window.print()}>Imprimir / PDF</Btn>
            <Btn variant="s" icon={MessageCircle} onClick={enviar}>{orc.status === "enviado" ? "Reenviar" : "Enviar pelo WhatsApp"}</Btn>
          </>}
          {situacao(orc) === "enviado" && <>
            <Btn variant="x" icon={XCircle} onClick={() => setRecusa(orc.id)}>Não fechou</Btn>
            <Btn variant="ok" icon={CheckCircle2} onClick={() => { setPlaca(""); setConverter(orc.id); }}>Cliente aprovou</Btn>
          </>}
          {situacao(orc) === "expirado" && <Btn variant="o" icon={RefreshCw}
            onClick={() => { mudar(orc.id, (o) => ({ ...o, status: "rascunho", criado: dias(0) })); toast("Orçamento reaberto", "Confira os preços das peças antes de reenviar."); }}>Refazer com preços de hoje</Btn>}
          {orc.os && <Btn variant="p" icon={ChevronRight} onClick={() => { setAberto(null); abrirOS(orc.os); }}>Abrir {orc.os}</Btn>}
        </>}>
        {orc && (() => {
          const editavel = ["rascunho", "enviado"].includes(situacao(orc));
          const subP = orc.itens.filter((i) => i.tipo === "peca").reduce((s, i) => s + i.qtd * i.valor, 0);
          const subS = orc.itens.filter((i) => i.tipo === "servico").reduce((s, i) => s + i.qtd * i.valor, 0);
          return (
            <div className="grid gap-4">
              <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))" }}>
                <Field label="Nome do cliente" hint={orc.cli ? "Cliente já cadastrado." : "Não precisa cadastrar agora."}>
                  <input className="inp" value={orc.nome} onChange={(e) => mudar(orc.id, (o) => ({ ...o, nome: e.target.value }))}
                    disabled={!editavel} placeholder="Quem ligou" />
                </Field>
                <Field label="WhatsApp">
                  <input className="inp inp-mono" value={orc.tel} onChange={(e) => mudar(orc.id, (o) => ({ ...o, tel: e.target.value }))}
                    disabled={!editavel} placeholder="(35) 90000-0000" />
                </Field>
                <Field label="Veículo" hint="Marca, modelo e ano bastam. A placa entra quando o carro chegar.">
                  <input className="inp" value={orc.veiculo} onChange={(e) => mudar(orc.id, (o) => ({ ...o, veiculo: e.target.value }))}
                    disabled={!editavel} placeholder="Gol 1.6 2019" />
                </Field>
                <Field label="Validade" hint="Preço de peça muda. Orçamento velho é prejuízo.">
                  <select className="inp" value={orc.validade} onChange={(e) => mudar(orc.id, (o) => ({ ...o, validade: +e.target.value }))} disabled={!editavel}>
                    {[7, 15, 30].map((d) => <option key={d} value={d}>{d} dias</option>)}
                  </select>
                </Field>
              </div>

              <div className="brd" style={{ borderRadius: "var(--r)", overflow: "hidden" }}>
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5" style={{ borderBottom: "1px solid var(--line)", background: "var(--el)" }}>
                  <span className="eyebrow">Itens</span>
                  {editavel && (
                    <div className="flex flex-wrap gap-1.5">
                      <Btn variant="s" size="xs" icon={Mic} onClick={() => setVoz(true)}>Ditar</Btn>
                      <Btn variant="o" size="xs" icon={Package} onClick={() => { setAddItem("peca"); setBusca(""); }}>Peça</Btn>
                      <Btn variant="o" size="xs" icon={Wrench} onClick={() => { setAddItem("servico"); setBusca(""); }}>Serviço</Btn>
                    </div>
                  )}
                </div>
                {orc.itens.length === 0
                  ? <div className="text-sm t-mut2 text-center py-7 px-4">Nenhum item ainda. Adicione pela busca ou dite por voz.</div>
                  : <table className="tbl">
                      <tbody>
                        {orc.itens.map((i) => (
                          <tr key={i.id}>
                            <td>
                              <span className="text-sm font-medium">{i.desc}</span>
                              <span className="block text-xs t-mut2">{i.tipo === "peca" ? "Peça" : "Serviço"}</span>
                            </td>
                            <td style={{ width: 66 }}>
                              {editavel ? <input className="inp inp-mono" style={{ padding: "3px 6px", fontSize: 13, textAlign: "right" }}
                                value={i.qtd} onChange={(e) => mudarQtd(i.id, Math.max(1, +e.target.value || 1))} />
                                : <span className="mono text-sm">{i.qtd}</span>}
                            </td>
                            <td className="num text-sm font-semibold" style={{ width: 96 }}>{brl(i.qtd * i.valor)}</td>
                            <td style={{ width: 34 }}>{editavel && <button className="btn btn-g btn-xs"
                              onClick={() => tirarLinha(i.id)}><Trash2 size={13} /></button>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>}
                <div className="grid gap-1.5 p-3 text-sm" style={{ borderTop: "1px solid var(--line)", background: "var(--el)" }}>
                  <div className="flex justify-between"><span className="t-mut">Peças</span><span className="mono">{brl(subP)}</span></div>
                  <div className="flex justify-between"><span className="t-mut">Serviços</span><span className="mono">{brl(subS)}</span></div>
                  <div className="flex justify-between items-center">
                    <span className="t-mut">Desconto</span>
                    <input className="inp inp-mono" style={{ width: 96, padding: "3px 8px", fontSize: 13, textAlign: "right" }}
                      value={orc.desconto} onChange={(e) => mudar(orc.id, (o) => ({ ...o, desconto: +e.target.value || 0 }))} disabled={!editavel} />
                  </div>
                  <div className="flex justify-between pt-1.5" style={{ borderTop: "1px solid var(--line2)" }}>
                    <span className="dsp" style={{ fontSize: 15 }}>Total</span>
                    <span className="mono font-semibold" style={{ fontSize: 17 }}>{brl(total(orc))}</span>
                  </div>
                  {admin && orc.itens.length > 0 && (
                    <div className="flex justify-between text-xs t-mut pt-1">
                      <span>Margem estimada</span>
                      <span className="mono">{total(orc) ? (((total(orc) - custo(orc)) / total(orc)) * 100).toFixed(0) : 0}%</span>
                    </div>
                  )}
                </div>
              </div>

              <Field label="Observações" hint="O que o cliente relatou. Vira a queixa da OS se ele aprovar.">
                <textarea className="inp" rows={2} value={orc.obs} onChange={(e) => mudar(orc.id, (o) => ({ ...o, obs: e.target.value }))} disabled={!editavel} />
              </Field>

              {orc.status === "recusado" && (
                <div className="p-3 text-sm" style={{ background: "var(--crit-l)", border: "1px solid #F1C9C5", borderRadius: "var(--r)" }}>
                  <span className="font-semibold">Não fechou: </span>
                  {MOTIVOS_RECUSA.find(([k]) => k === orc.motivo)?.[1] ?? "motivo não registrado"}
                  {orc.obs && <span className="block text-xs t-mut mt-1">{orc.obs}</span>}
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* prévia da mensagem */}
      <Modal open={!!zap} onClose={() => setZap(null)} w="max-w-lg"
        title="WhatsApp aberto com esta mensagem"
        sub={zap ? `${db.orcamentos.find((o) => o.id === zap)?.tel} — confira e aperte enviar por lá` : ""}
        footer={<>
          <Btn variant="o" icon={Copy}
            onClick={() => copiar(mensagem(db.orcamentos.find((o) => o.id === zap)), toast, "Mensagem")}>Copiar texto</Btn>
          <Btn variant="p" onClick={() => setZap(null)}>Fechar</Btn>
        </>}>
        {zap && (
          <>
            <div className="mono text-sm p-3.5" style={{ background: "#E6F5DE", border: "1px solid #CBE5BE",
              borderRadius: "10px 10px 10px 2px", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
              {mensagem(db.orcamentos.find((o) => o.id === zap))}
            </div>
            <div className="text-xs t-mut2 mt-3">
              O cliente responde e você registra aqui. Quando aprovar, o orçamento vira ordem de serviço e as peças são reservadas.
            </div>
          </>
        )}
      </Modal>

      {/* recusa */}
      <Modal open={!!recusa} onClose={() => setRecusa(null)} w="max-w-md"
        title="Por que não fechou?" sub="Trinta segundos agora valem um relatório inteiro daqui a três meses.">
        <div className="grid gap-1.5">
          {MOTIVOS_RECUSA.map(([k, l]) => (
            <button key={k} onClick={() => confirmarRecusa(k)} className="flex items-center gap-3 px-3 py-3 text-left"
              style={{ border: "1px solid var(--line)", borderRadius: "var(--r)", background: "var(--card)", cursor: "pointer" }}>
              <XCircle size={15} className="t-mut2" />
              <span className="text-sm font-medium flex-1">{l}</span>
              <ChevronRight size={15} className="t-mut2" />
            </button>
          ))}
        </div>
      </Modal>

      {/* conversão em OS */}
      <Modal open={!!converter} onClose={() => setConverter(null)} w="max-w-md"
        title="Cliente aprovou" sub="O carro está chegando. Só falta a placa."
        footer={<><Btn variant="g" onClick={() => setConverter(null)}>Cancelar</Btn>
          <Btn variant="ok" icon={Wrench} onClick={converterEmOS}>Abrir ordem de serviço</Btn></>}>
        {converter && (() => {
          const o = db.orcamentos.find((x) => x.id === converter);
          return (
            <div className="grid gap-3.5">
              <div className="flex justify-center"><Placa n={placa || "ABC0D00"} size="lg" /></div>
              <Field label="Placa do veículo">
                <input className="inp inp-mono" style={{ textTransform: "uppercase" }} value={placa}
                  onChange={(e) => setPlaca(e.target.value.toUpperCase())} placeholder="ABC1D23" maxLength={8} autoFocus />
              </Field>
              <div className="grid gap-1.5 p-3 text-sm" style={{ background: "var(--el)", border: "1px solid var(--line)", borderRadius: "var(--r)" }}>
                <div className="flex justify-between"><span className="t-mut">Cliente</span><span className="font-medium">{o.nome}</span></div>
                <div className="flex justify-between"><span className="t-mut">Veículo</span><span className="font-medium">{o.veiculo}</span></div>
                <div className="flex justify-between"><span className="t-mut">Total</span><span className="mono font-semibold">{brl(total(o))}</span></div>
              </div>
              <div className="flex items-start gap-2.5 p-3 text-sm" style={{ background: "var(--gulf-l)", border: "1px solid #BEE0EC", borderRadius: "var(--r)" }}>
                <Info size={15} className="t-gulf mt-0.5 flex-none" />
                <span>
                  {o.cli ? "O cliente já está cadastrado. " : "O cliente vai ser cadastrado automaticamente. "}
                  Os itens vão para a OS, o título entra em contas a receber e as peças ficam reservadas.
                </span>
              </div>
            </div>
          );
        })()}
      </Modal>

      {orc && <VozOrcamento open={voz} onClose={() => setVoz(false)} db={db} aplicar={aplicarVoz} toast={toast}
        mecanico={db.usuarios.find((u) => u.id === "U2")} />}

      <Modal open={!!addItem} onClose={() => setAddItem(null)} title={addItem === "peca" ? "Adicionar peça" : "Adicionar serviço"}>
        <Busca v={busca} set={setBusca} ph="Nome ou código…" />
        <div className="grid gap-1.5 mt-3" style={{ maxHeight: 340, overflowY: "auto" }}>
          {(addItem === "peca" ? db.pecas : db.servicos)
            .filter((x) => !busca || (x.nome + x.cod).toLowerCase().includes(busca.toLowerCase()))
            .map((x) => (
              <button key={x.id} className="flex items-center gap-3 px-3 py-2.5 text-left"
                style={{ border: "1px solid var(--line)", borderRadius: "var(--r)", background: "var(--card)", cursor: "pointer" }}
                onClick={() => {
                  addLinha(addItem === "peca"
                    ? { tipo: "peca", ref: x.id, desc: x.nome, qtd: 1, valor: x.venda, custo: x.custo }
                    : { tipo: "servico", ref: x.id, desc: x.nome, qtd: 1, valor: x.valor, custo: 0 });
                  setAddItem(null);
                }}>
                <span className="mono text-xs t-mut2" style={{ width: 88 }}>{x.cod}</span>
                <span className="flex-1 text-sm font-medium">{x.nome}</span>
                <span className="mono text-sm font-semibold">{brl(addItem === "peca" ? x.venda : x.valor)}</span>
              </button>
            ))}
        </div>
      </Modal>
    </div>
  );
}

export { Orcamentos };
